import type { FC } from "react";
import { forwardRef, useEffect, useState } from "react";
import { Button, Input, Loader, Modal, Uploader } from "rsuite";
import { Company } from "../../../models/company/company";
import { BsCamera, BsUpload } from "react-icons/bs";
import { errorToast, successToast } from "../../../utils/helper-ui";
import { asyncLocalStorage } from "../../../utils/storage";

interface CompanyFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Company) => void;
  company?: Company;
}

const CompanyForm: FC<CompanyFormProps> = ({
  open,
  onClose,
  onSubmit,
  company,
}) => {
  const [data, setData] = useState<Company>({
    id: "",
    name: "",
    logo: "",
    cover: "",
    legal_entity: "",
    email: "",
    phone: "",
    fax: "",
    address: "",
    contact_person: "",
    contact_person_position: "",
    status: "",
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [token, setToken] = useState("");
  useEffect(() => {
    asyncLocalStorage.getItem("token").then((token) => {
      setToken(token);
    });
  }, []);

  
  useEffect(() => {
    setData(
      company || {
        id: "",
        name: "",
        logo: "",
        cover: "",
        legal_entity: "",
        email: "",
        phone: "",
        fax: "",
        address: "",
        contact_person: "",
        contact_person_position: "",
        status: "",
      }
    );
  }, [company]);

  const submit = () => {
    onSubmit(data);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <Modal.Header>
        <Modal.Title>{company ? "Update" : "Create"} Company</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group">
          <label>Name</label>
          <Input
            name="name"
            value={data.name}
            onChange={(value) => setData({ ...data, name: value })}
          />
        </div>
        <div className="form-group">
          <label>Logo</label>
          <Uploader
            fileListVisible={false}
            listType="picture"
            accept="image/*"
            action={process.env.REACT_APP_API_URL + "/v1/file/upload"}
            onUpload={(file) => {
              setUploading(true);
            }}
            headers={{
              Authorization: `Bearer ${token}`,
            }}
            onSuccess={(response, file) => {
              setUploading(false);
              successToast("File uploaded successfully");
              setData({ ...data, logo: response.file.url });
            }}
            onError={() => {
              setUploading(false);
              errorToast("Upload failed");
            }}
          >
            <button style={{ width: 150, height: 150 }}>
              {uploading && <Loader backdrop center />}
              {data.logo ? (
                <img src={data.logo} width="100%" height="100%" />
              ) : (
                <BsCamera style={{ fontSize: 80 }} />
              )}
            </button>
          </Uploader>
        </div>
        <div className="form-group">
          <label>Cover</label>
          <Uploader
            fileListVisible={false}
            listType="picture"
            accept="image/*"
            action={process.env.REACT_APP_API_URL + "/v1/file/upload"}
            onUpload={(file) => {
              setUploadingCover(true);
            }}
            headers={{
              Authorization: `Bearer ${token}`,
            }}
            onSuccess={(response, file) => {
              setUploadingCover(false);
              successToast("File uploaded successfully");
              setData({ ...data, cover: response.file.url });
            }}
            onError={() => {
              setUploadingCover(false);
              errorToast("Upload failed");
            }}
          >
            <button style={{ width: 150, height: 150 }}>
              {uploadingCover && <Loader backdrop center />}
              {data.cover ? (
                <img src={data.cover} width="100%" height="100%" />
              ) : (
                <BsCamera style={{ fontSize: 80 }} />
              )}
            </button>
          </Uploader>
        </div>
        <div className="form-group">
          <label>Legal Entity</label>
          <Input
            name="legal_entity"
            value={data.legal_entity}
            onChange={(val) => setData({ ...data, legal_entity: val })}
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <Input
            name="email"
            type="email"
            value={data.email}
            onChange={(val) => setData({ ...data, email: val })}
          />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <Input
            name="phone"
            value={data.phone}
            onChange={(val) => setData({ ...data, phone: val })}
          />
        </div>
        <div className="form-group">
          <label>Fax</label>
          <Input
            name="fax"
            value={data.fax}
            onChange={(val) => setData({ ...data, fax: val })}
          />
        </div>
        <div className="form-group">
          <label>Address</label>
          <Input
            as={"textarea"}
            rows={5}
            name="address"
            value={data.address}
            onChange={(val) => setData({ ...data, address: val })}
          />
        </div>
        <div className="form-group">
          <label>Contact Person</label>
          <Input
            name="contact_person"
            value={data.contact_person}
            onChange={(val) => setData({ ...data, contact_person: val })}
          />
        </div>
        <div className="form-group">
          <label>Contact Person Position</label>
          <Input
            name="contact_person_position"
            value={data.contact_person_position}
            onChange={(val) =>
              setData({ ...data, contact_person_position: val })
            }
          />
        </div>
        <div className="form-group">
          <label>Status</label>
          <Input
            name="status"
            value={data.status}
            onChange={(val) => setData({ ...data, status: val })}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="subtle">
          Cancel
        </Button>
        <Button onClick={submit} appearance="primary">
          {company ? "Update" : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CompanyForm;
